import { useEffect, useState } from "react";
import type { JSX, KeyboardEvent } from "react";
import { useApp } from "@/contexts/AppContext";
import type { UpdateDeviceTagsResponse } from "../types";

type TagsEditorProps = {
  deviceId: string;
  initialTags: string[];
  onSaved?: (tags: string[]) => void;
};

export const TagsEditor = ({
  deviceId,
  initialTags,
  onSaved,
}: TagsEditorProps): JSX.Element => {
  const { performRequest, refreshAvailableData } = useApp();
  const [tags, setTags] = useState<string[]>(initialTags);
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setTags(initialTags);
  }, [initialTags, deviceId]);

  const persist = async (next: string[]): Promise<void> => {
    setIsSaving(true);
    setErrorMessage("");
    try {
      const response = await performRequest<UpdateDeviceTagsResponse>(
        `/devices/${encodeURIComponent(deviceId)}/tags`,
        {
          method: "PATCH",
          body: JSON.stringify({ tags: next }),
        },
      );
      const persisted = Array.isArray(response?.tags) ? response.tags : next;
      setTags(persisted);
      onSaved?.(persisted);
      void refreshAvailableData();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to save tags",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const commitDraft = (): void => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (tags.includes(trimmed)) {
      setDraft("");
      return;
    }
    const next = [...tags, trimmed];
    setDraft("");
    void persist(next);
  };

  const removeTag = (tag: string): void => {
    const next = tags.filter((value) => value !== tag);
    void persist(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commitDraft();
    } else if (event.key === "Backspace" && draft === "" && tags.length > 0) {
      const last = tags[tags.length - 1];
      if (last !== undefined) removeTag(last);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-2 py-1.5 transition-colors focus-within:border-(--brand-primary)">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-(--radius-pill) bg-(--pastel-violet) py-0.5 pl-2 pr-1 text-[12px] font-semibold text-(--brand-primary)"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              disabled={isSaving}
              aria-label={`Remove ${tag}`}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[13px] leading-none text-(--brand-primary)/70 transition-colors hover:bg-(--brand-primary)/15 hover:text-(--brand-primary) disabled:opacity-50"
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitDraft}
          placeholder={tags.length === 0 ? "Add tag and press Enter…" : "Add…"}
          disabled={isSaving}
          className="min-w-[100px] flex-1 appearance-none border-0 bg-transparent py-0.5 text-[13px] text-(--text-primary) placeholder:text-(--text-muted) outline-none"
        />
      </div>
      {errorMessage ? (
        <p className="text-[12px] text-(--status-danger)">{errorMessage}</p>
      ) : null}
    </div>
  );
};
